import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  evaluateVisualizationOptions,
  getWorksheetUrlParams,
  isWorksheetUrl,
} from '@/annotation/reportContent.utilities';
import { sqExploreWorkbookModalStore } from '@/core/core.stores';
import { ExplorerModalBody } from '@/explorer/ExplorerModalBody.molecule';
import * as exploreWorkbookModalActions from '@/explorer/exploreWorkbookModal.actions';
import { setSourceUrl, setWorkbookId, setWorksheetId, setWorkstepId } from '@/reportEditor/reportContent.actions';
import { useReportContentSourceUrl } from '@/reportEditor/reportContent.store';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, TextArea } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';

interface ReportContentWorksheetAndUrlProps {
  modalLayout?: boolean;
  selectOnlyWorkbooks?: boolean;
  setSelectedItem?: (item: any) => void;
  extraClassNames?: string;
  allowedTypes?: string[];
}

export const ReportContentWorksheetAndUrl = ({
  selectOnlyWorkbooks = false,
  modalLayout = false,
  setSelectedItem,
  extraClassNames,
  allowedTypes,
}: ReportContentWorksheetAndUrlProps) => {
  const { t } = useTranslation();
  const sourceUrl = useReportContentSourceUrl();
  const [_invalidReason, setInvalidReason] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);
  const [currentContentUrl, setCurrentContentUrl] = useState(sourceUrl);

  const validateContentUrl = (url: string) => {
    setCurrentContentUrl(url);
    setInvalidReason('');
    if (_.isEmpty(url)) {
      setIsInvalid(false);
      return;
    }
    if (!_.isEmpty(url) && !isWorksheetUrl(url)) {
      setIsInvalid(true);
      setSourceUrl(undefined);
    } else {
      setSourceUrl(url);
      getWorksheetUrlParams(url)
        .then((params) => {
          setIsInvalid(false);
          setWorkbookId(params.workbookId, params.workbookType);
          setWorksheetId(params.worksheetId);
          setWorkstepId(params.workstepId);
          evaluateVisualizationOptions(params.workbookId, params.worksheetId, params.workstepId);
        })
        .catch((e) => {
          setIsInvalid(true);
          setInvalidReason(`${e?.data?.statusMessage || e} -`);
          setSourceUrl(undefined);
        });
    }
  };

  return (
    <div
      className={clsx(
        'flexRowContainer',
        extraClassNames,
        stylex.props(modalLayout && selectOnlyWorkbooks && reportContentUrlStyles.workbookSelector).className,
      )}>
      <ExplorerModalBody
        show={true}
        allowedTypes={allowedTypes}
        setSelectedItem={setSelectedItem}
        isSelectable={selectOnlyWorkbooks}
        selectOnlyWorkbooks={selectOnlyWorkbooks}
        actions={exploreWorkbookModalActions}
        next={() => {
          setSourceUrl(undefined);
          // The idea here is to get the latest values when the user clicks "Next".
          const { workbookId, workbookType } = sqExploreWorkbookModalStore.getState();
          setWorkbookId(workbookId, workbookType);
        }}
      />
      {!selectOnlyWorkbooks && (
        <div>
          <div {...stylex.props(reportContentUrlStyles.sourceSeparator)}>
            <h5
              {...stylex.props(modalLayout && reportContentUrlStyles.separatorHeading)}
              className={clsx(
                modalLayout && 'reportContentSourceSeparator',
                stylex.props(modalLayout && reportContentUrlStyles.separatorHeading).className,
              )}>
              <span {...stylex.props(modalLayout && reportContentUrlStyles.separatorLabel)}>{t('OR')}</span>
            </h5>
          </div>
          <div className="flexRowContainer" data-testid="report-content-link">
            <div>
              <h4>
                {t('REPORT.CONTENT.LINK')}
                <Icon
                  icon={faQuestionCircle}
                  xstyle={reportContentUrlStyles.linkHelp}
                  tooltip={t('REPORT.CONTENT.LINK_HELP')}
                  tooltipPlacement="top"
                  testId="linkHelpIcon"
                />
              </h4>
            </div>
            <TextArea
              extraClassNames="flexFill"
              xstyle={[
                reportContentUrlStyles.urlField,
                modalLayout && reportContentUrlStyles.modalUrlField,
                isInvalid && reportContentUrlStyles.invalidSource,
                !isInvalid && !_.isEmpty(currentContentUrl) && reportContentUrlStyles.validSource,
                modalLayout &&
                  (isInvalid || !_.isEmpty(currentContentUrl)) &&
                  reportContentUrlStyles.validatedSourceText,
              ]}
              showError={isInvalid}
              placeholder={t('REPORT.CONTENT.LINK_PLACEHOLDER')}
              autoFocus
              onChange={(e) => validateContentUrl(e.target.value)}
              value={currentContentUrl}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const reportContentUrlStyles = stylex.create({
  workbookSelector: { minWidth: 530, maxWidth: 530, width: 530 },
  separatorHeading: {
    width: '100%',
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: colors.organizerContentSeparator,
    lineHeight: '0.1px',
    fontSize: '0.8rem',
    marginTop: 10,
    marginRight: 0,
    marginBottom: 20,
    marginLeft: 0,
  },
  separatorLabel: {
    backgroundColor: colors.background,
    paddingTop: 0,
    paddingRight: 10,
    paddingBottom: 0,
    paddingLeft: 10,
  },
  linkHelp: {
    marginLeft: 5,
    marginRight: 5,
    color: {
      'default': colors.legacyToolsLink,
      ':hover': colors.legacyToolsPrimary,
      ':focus': colors.legacyToolsPrimary,
      ':active': colors.legacyToolsPrimary,
    },
  },
  modalUrlField: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.toolsDisabledBorder,
    borderRadius: 3,
    textAlign: 'center',
    paddingTop: 15,
    resize: 'none',
  },
  invalidSource: { backgroundColor: colors.organizerInvalidSource },
  validSource: { backgroundColor: colors.organizerValidSource },
  validatedSourceText: { color: 'initial' },
  urlField: { marginBottom: '0.3125rem' },
  sourceSeparator: {
    marginTop: '1.25rem',
  },
});
